import { cn } from '../../utils/cn';

function clampPct(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(100, num));
}

/**
 * Barra de progresso dos heroes (`.pen` → `ds/feedback/progress-bar`,
 * raio `$radius-ds-full`). O master tem 8px de altura, mas cada instância
 * define a sua: `ds/card/cycle-header` › `track` = 4, os heroes de ciclo e
 * `ds/card/cycle-summary` = 6.
 *
 * Cores por instância (o preenchimento é sempre liso — o gradiente do master
 * é sobreposto pelo `fill` da instância):
 * - `filled`     → dentro do hero em gradiente: track `$on-hero-track`,
 *                  preenchimento `$on-hero`
 * - `warning`    → hero `--tight`: track `$on-hero-track`, preenchimento
 *                  `$warning-500`
 * - `heroDanger` → dentro do hero sage em excesso
 *                  (`ds/card/budget-allocation-hero--excesso`): track
 *                  `$on-hero-track`, preenchimento sólido `$warning-500` —
 *                  usado pelo hero de Orçamento
 * - `surface`    → sobre superfície neutra (`cycle-header`, `cycle-summary`):
 *                  track `$track`, preenchimento `$sage-500`
 *
 * Simples por natureza: um único preenchimento, sem marcas/ticks sobrepostos
 * — para barras com marcas de referência, ver `ds/bar/marked-progress` no
 * `.pen`.
 *
 * `fillColor`/`trackColor` (cor CSS, ex. hex) e `height`/`radius` (px /
 * classe Tailwind) permitem reaproveitar esta barra fora dos heroes — ex.
 * barras de grupo/categoria em Orçamento, com a cor dinâmica do grupo — sem
 * afectar o visual por omissão dos consumidores existentes (`size` continua
 * a mandar quando `height` não é passado).
 */
const VARIANTS = {
  filled: { track: 'bg-sage-50/20', fill: 'bg-sage-50' },
  warning: { track: 'bg-sage-50/20', fill: 'bg-warning-500' },
  heroDanger: { track: 'bg-sage-50/20', fill: 'bg-warning-500' },
  surface: { track: 'bg-track', fill: 'bg-sage-500' },
};

/** Alturas do `.pen`: 4 (spine do ciclo), 6 (heroes), 8 (master). */
const SIZES = {
  sm: 'h-1',
  md: 'h-1.5',
  lg: 'h-2',
};

/**
 * @param {{
 *   variant?: 'filled' | 'warning' | 'heroDanger' | 'surface',
 *   size?: 'sm' | 'md' | 'lg',
 *   percentage?: number,
 *   className?: string,
 *   ariaLabel?: string,
 *   fillColor?: string,
 *   fillClassName?: string,
 *   trackColor?: string,
 *   trackClassName?: string,
 *   height?: number,
 *   radius?: string,
 *   'data-testid'?: string,
 * }} props
 */
export function HeroProgressBar({
  variant = 'surface',
  size = 'md',
  percentage = 0,
  className,
  ariaLabel,
  fillColor,
  fillClassName,
  trackColor,
  trackClassName,
  height,
  radius = 'rounded-ds-full',
  'data-testid': dataTestId,
}) {
  const width = Math.round(clampPct(percentage));
  const tokens = VARIANTS[variant] || VARIANTS.surface;
  const trackStyle = {
    ...(height ? { height } : null),
    ...(trackColor ? { backgroundColor: trackColor } : null),
  };

  return (
    <div
      role="progressbar"
      aria-valuenow={width}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
      data-ds-id="ds/feedback/progress-bar"
      data-testid={dataTestId}
      className={cn(
        'w-full overflow-hidden',
        height ? undefined : SIZES[size] || SIZES.md,
        radius,
        trackColor ? undefined : trackClassName || tokens.track,
        className,
      )}
      style={Object.keys(trackStyle).length ? trackStyle : undefined}
    >
      <div
        className={cn(
          'h-full transition-[width]',
          radius,
          fillColor ? undefined : fillClassName || tokens.fill,
        )}
        style={{ width: `${width}%`, ...(fillColor ? { backgroundColor: fillColor } : null) }}
      />
    </div>
  );
}

export default HeroProgressBar;
